import apiClient from "../../services/apiClient";
import { useEffect, useState, useContext, useMemo, useRef } from "react";
import {
  Table,
  Spin,
  Alert,
  Button,
  Input,
  Select,
  Card,
  Tag,
  Tooltip,
  Modal,
  Pagination,
  Descriptions,
  Form,
  message,
  Popconfirm,
} from "antd";
import { ThemeContext } from "../../context/ThemeContext";
import {
  BarChartOutlined,
  ReloadOutlined,
  DownloadOutlined,
  EyeOutlined,
  RiseOutlined,
  DollarOutlined,
  SearchOutlined,
  InfoCircleOutlined,
  SortAscendingOutlined,
  CrownOutlined,
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import "./NSDL.css";
import { useNavigate } from "react-router-dom";

const { Search } = Input;
const { Option } = Select;

// Utility function to break camelCase keys into readable format
const camelCaseToReadable = (str) => {
  return str
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .replace(/^./, (match) => match.toUpperCase());
};

const PreferenceShares = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [govSecurities, setGovSecurities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [selectedIssuer, setSelectedIssuer] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [sortBy, setSortBy] = useState("companyName");
  const [viewMode, setViewMode] = useState("table");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
  });
  const { isDark } = useContext(ThemeContext);
  const navigate = useNavigate();

  // State for edit modal
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const editForm = useRef();

  // Fetch paginated data from backend
  const fetchPreferenceShares = async (page = 1, pageSize = 20) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(
        `${baseUrl}/nsdl/preferenceshares?page=${page - 1}&size=${pageSize}`,
        { validateStatus: () => true }
      );
      if (response.status === 204) {
        setGovSecurities([]);
        setPagination({ current: 1, pageSize, total: 0, totalPages: 0 });
        return;
      }
      if (response.data && (response.data.content || response.data.data)) {
        const data = response.data.content ? response.data : response.data.data;
        setGovSecurities(Array.isArray(data.content) ? data.content : []);
        setPagination({
          current: (data.pageNumber || 0) + 1,
          pageSize: data.pageSize || pageSize,
          total: data.totalElements || 0,
          totalPages: data.totalPages || 0,
        });
      } else {
        throw new Error(response.data.message || "Failed to fetch data");
      }
    } catch (error) {
      setError("Failed to load preference shares data. Please try again.");
      setGovSecurities([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPreferenceShares(pagination.current, pagination.pageSize);
    // eslint-disable-next-line
  }, []);

  // Filtering and sorting (client-side for current page)
  const filteredData = useMemo(() => {
    let filtered = Array.isArray(govSecurities) ? govSecurities : [];
    if (!Array.isArray(filtered)) return [];
    if (searchText) {
      filtered = filtered.filter((item) => {
        const searchLower = searchText.toLowerCase();
        return (
          (item.companyName && item.companyName.toLowerCase().includes(searchLower)) ||
          (item.descriptionInNsdl && item.descriptionInNsdl.toLowerCase().includes(searchLower)) ||
          (item.isin && item.isin.toLowerCase().includes(searchLower))
        );
      });
    }
    if (selectedIssuer !== "all") {
      filtered = filtered.filter((item) => item.companyName === selectedIssuer);
    }
    if (selectedStatus !== "all") {
      filtered = filtered.filter((item) => item.listingStatus === selectedStatus);
    }
    if (Array.isArray(filtered)) {
      filtered = [...filtered].sort((a, b) => {
        let aValue = a[sortBy];
        let bValue = b[sortBy];
        if (aValue === undefined) aValue = '';
        if (bValue === undefined) bValue = '';
        if (typeof aValue === "number" && typeof bValue === "number") {
          return bValue - aValue;
        }
        return String(aValue).localeCompare(String(bValue));
      });
    }
    return filtered;
  }, [govSecurities, searchText, selectedIssuer, selectedStatus, sortBy]);

  // Unique values for filters (from current page)
  const { issuers, statuses } = useMemo(() => {
    const uniqueIssuers = [
      ...new Set(govSecurities.map((item) => item.companyName)),
    ]
      .filter(Boolean)
      .slice(0, 50);
    const uniqueStatuses = [
      ...new Set(govSecurities.map((item) => item.listingStatus)),
    ].filter(Boolean);
    return { issuers: uniqueIssuers, statuses: uniqueStatuses };
  }, [govSecurities]);

  // Pagination change handler
  const handlePageChange = (page, pageSize) => {
    fetchPreferenceShares(page, pageSize);
  };

  // Memoized stats
  const stats = useMemo(
    () => [
      {
        title: "Total Shares",
        value: pagination.total.toLocaleString(),
        icon: <CrownOutlined />,
        color: "#4ecdc4",
      },
      {
        title: "Active Issues",
        value: govSecurities
          .filter((item) => item.listingStatus === "Active")
          .length.toLocaleString(),
        icon: <RiseOutlined />,
        color: "#667eea",
      },
      {
        title: "Total Volume",
        value:
          "₹" +
          (
            govSecurities.reduce(
              (sum, item) => sum + (parseFloat(item.totalIssueSize) || 0),
              0
            ) / 10000000
          ).toFixed(1) +
          " Cr",
        icon: <DollarOutlined />,
        color: "#ff6b6b",
      },
    ],
    [govSecurities, pagination.total]
  );

  const handleRefresh = () => {
    fetchPreferenceShares(pagination.current, pagination.pageSize);
  };

  const handleExport = () => {
    console.log("Export functionality");
  };

  const showRecordDetails = (record) => {
    setSelectedRecord(record);
    setDetailModalVisible(true);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "no":
        return "green";
      case "yes":
        return "red";
      default:
        return "default";
    }
  };

  const formatValue = (value, key) => {
    if (value === null || value === undefined || value === "nan") return "N/A";

    if (key.toLowerCase().includes("size") && typeof value === "number") {
      return `₹${(value / 10000000).toFixed(2)} Cr`;
    }

    if (
      key.toLowerCase().includes("rate") ||
      key.toLowerCase().includes("coupon")
    ) {
      return `${value}%`;
    }

    if (
      key.toLowerCase().includes("date") ||
      key.toLowerCase().includes("redemption")
    ) {
      return new Date(value).toLocaleDateString();
    }

    return String(value);
  };

  const handleEdit = (record) => {
    setEditData(record);
    setEditModalVisible(true);
  };
  const handleUpdate = async (values) => {
    setEditLoading(true);
    try {
      await apiClient.put(`${baseUrl}/nsdl/preferenceshares/${editData.isin}`, values);
      message.success('Record updated successfully');
      setEditModalVisible(false);
      fetchPreferenceShares(pagination.current, pagination.pageSize); // Refresh current page
    } catch (error) {
      message.error('Failed to update record');
    } finally {
      setEditLoading(false);
    }
  };
  const handleDelete = async (isin) => {
    try {
      await apiClient.delete(`${baseUrl}/nsdl/preferenceshares/${isin}`);
      message.success('Record deleted successfully');
      fetchPreferenceShares(pagination.current, pagination.pageSize); // Refresh current page
    } catch (error) {
      message.error('Failed to delete record');
    }
  };

  return (
    <div className={`nsdl-container ${isDark ? "dark" : ""}`}>
      <Button
        type="default"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/d/nsdl")}
        style={{ marginBottom: 16 }}
      >
        Back to NSDL
      </Button>
      {/* Hero Section */}
      <div
        className="nsdl-hero"
        style={{
          background: 'linear-gradient(90deg, #3AA015 0%, #F49A24 50%, rgba(255, 0, 4, 0.61) 100%)',
          borderRadius: 18,
          padding: '2px',
          marginBottom: 32,
          position: 'relative',
        }}
      >
        <div
          style={{
            background: 'transparent',
            borderRadius: 16,
            padding: "48px 48px 40px 48px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
            position: 'relative',
            overflow: 'hidden',
            minHeight: 180,
            width: '100%',
            height: '100%',
          }}
        >
          {/* Overlay for better text readability */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: isDark 
                ? 'linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.5) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
              borderRadius: 16,
            }}
          />

          {/* Content */}
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h1
                style={{
                  margin: 0,
                  marginBottom: 12,
                  fontWeight: 800,
                  fontSize: 42,
                  color: isDark ? "#ffffff" : "#1a1a1a",
                }}
              >
                Preference Shares
              </h1>
              <p
                style={{
                  fontSize: 20,
                  color: isDark ? "rgba(255, 255, 255, 0.9)" : "#4a4a4a",
                  fontWeight: 400,
                  lineHeight: 1.4,
                  margin: 0,
                }}
              >
                Hybrid securities with priority over common shares, offering fixed dividends and comprehensive financial data
              </p>
            </div>

            {/* Stats on the right side - 2 per row */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'flex-end' }}>
              {/* First row - 2 stats */}
              <div style={{ display: 'flex', gap: '12px' }}>
                {stats.slice(0, 2).map((stat, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)',
                    padding: '12px 16px',
                    borderRadius: 12,
                    boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                    minWidth: '180px'
                  }}>
                    <div
                      style={{
                        color: stat.color,
                        fontSize: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.9)',
                        borderRadius: 8,
                        boxShadow: isDark ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      {stat.icon}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <span style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: isDark ? "#ffffff" : "#1a1a1a",
                        lineHeight: 1
                      }}>
                        {stat.value}
                      </span>
                      <span style={{
                        fontSize: 12,
                        color: isDark ? "rgba(255, 255, 255, 0.8)" : "#666",
                        fontWeight: 500
                      }}>
                        {stat.title}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Second row - remaining stats */}
              {stats.length > 2 && (
                <div style={{ display: 'flex', gap: '12px' }}>
                  {stats.slice(2).map((stat, index) => (
                    <div key={index + 2} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)',
                      padding: '12px 16px',
                      borderRadius: 12,
                      boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                      minWidth: '180px'
                    }}>
                      <div
                        style={{
                          color: stat.color,
                          fontSize: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 36,
                          height: 36,
                          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.9)',
                          borderRadius: 8,
                          boxShadow: isDark ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
                        }}
                      >
                        {stat.icon}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <span style={{
                          fontSize: 20,
                          fontWeight: 700,
                          color: isDark ? "#ffffff" : "#1a1a1a",
                          lineHeight: 1
                        }}>
                          {stat.value}
                        </span>
                        <span style={{
                          fontSize: 12,
                          color: isDark ? "rgba(255, 255, 255, 0.8)" : "#666",
                          fontWeight: 500
                        }}>
                          {stat.title}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Data Section */}
      <div className="nsdl-section">

        {/* Controls */}
        <div className="nsdl-controls">
          <div className="nsdl-controls-left">
            <Search
              placeholder="Search by company, instrument name, or ISIN..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
              prefix={<SearchOutlined />}
            />

            <Select
              value={selectedIssuer}
              onChange={setSelectedIssuer}
              placeholder="Filter by Company"
              style={{ width: 200 }}
              allowClear
            >
              <Option value="all">All Companies</Option>
              {issuers.map((issuer) => (
                <Option key={issuer} value={issuer}>
                  {issuer}
                </Option>
              ))}
            </Select>

            <Select
              value={selectedStatus}
              onChange={setSelectedStatus}
              placeholder="Filter by Status"
              style={{ width: 150 }}
              allowClear
            >
              <Option value="all">All Status</Option>
              {statuses.map((status) => (
                <Option key={status} value={status}>
                  {status}
                </Option>
              ))}
            </Select>

            <Select
              value={sortBy}
              onChange={setSortBy}
              placeholder="Sort by"
              style={{ width: 150 }}
            >
              <Option value="companyName">Company</Option>
              <Option value="totalIssueSize">Issue Size</Option>
              <Option value="redemption">Maturity Date</Option>
              <Option value="couponRate">Coupon Rate</Option>
            </Select>
          </div>

          <div className="nsdl-controls-right">
            <Button.Group>
              <Button
                type={viewMode === "cards" ? "primary" : "default"}
                icon={<BarChartOutlined />}
                onClick={() => setViewMode("cards")}
              >
                Cards
              </Button>
              <Button
                type={viewMode === "table" ? "primary" : "default"}
                icon={<SortAscendingOutlined />}
                onClick={() => setViewMode("table")}
              >
                Table
              </Button>
            </Button.Group>

            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
            >
              Refresh
            </Button>

            <Button icon={<DownloadOutlined />} onClick={handleExport}>
              Export
            </Button>
          </div>
        </div>

        {/* Results Info */}
        <div className="nsdl-results-info">
          <span>
            Showing {filteredData.length} of {pagination.total} shares (Page {pagination.current} of {pagination.totalPages})
          </span>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: "1rem" }}
          />
        )}

        {/* Data Display */}
        <div className="nsdl-data-container">
          {loading ? (
            <div className="nsdl-loading">
              <Spin size="large" />
              <p>Loading preference shares data...</p>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="nsdl-empty">
              <InfoCircleOutlined />
              <p>No preference shares found matching your criteria</p>
            </div>
          ) : viewMode === "cards" ? (
            <div className="nsdl-cards-grid">
              {filteredData.map((record, index) => (
                <Card key={index} className="nsdl-data-card" hoverable>
                  <div className="nsdl-card-header">
                    <h3 className="nsdl-card-title">
                      <span className="nsdl-isin-code">
                        {record.isin || `ISIN ${index + 1}`}
                      </span>
                    </h3>
                  </div>
                  <div className="nsdl-card-content">
                    <div className="nsdl-card-field">
                      <span className="nsdl-field-label">Company:</span>
                      <span className="nsdl-field-value nsdl-company-name">
                        {record.company || "N/A"}
                      </span>
                    </div>
                    <div className="nsdl-card-field">
                      <span className="nsdl-field-label">Instrument:</span>
                      <span className="nsdl-field-value">
                        {record.nameOfInstrument || "N/A"}
                      </span>
                    </div>
                    <div className="nsdl-card-field">
                      <span className="nsdl-field-label">Face Value:</span>
                      <span className="nsdl-field-value">
                        {record.faceValue ? `₹${(+record.faceValue).toLocaleString()}` : "N/A"}
                      </span>
                    </div>
                    <div className="nsdl-card-field">
                      <span className="nsdl-field-label">Issue Date:</span>
                      <span className="nsdl-field-value">
                        {record.issueDate || "N/A"}
                      </span>
                    </div>
                    <div className="nsdl-card-field">
                      <span className="nsdl-field-label">Redemption:</span>
                      <span className="nsdl-field-value">
                        {record.redemption || "N/A"}
                      </span>
                    </div>
                  </div>
                  <div className="card-actions" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16 }}>
                    <Tooltip title="View">
                      <Button
                        type="primary"
                        icon={<EyeOutlined />}
                        onClick={() => showRecordDetails(record)}
                      />
                    </Tooltip>
                    <Tooltip title="Edit">
                      <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                      />
                    </Tooltip>
                    <Tooltip title="Delete">
                      <Popconfirm
                        title="Are you sure you want to delete this record?"
                        onConfirm={() => handleDelete(record.isin)}
                        onCancel={() => message.error('Delete cancelled')}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Tooltip>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Table
              dataSource={filteredData}
              columns={[
                {
                  title: "ISIN",
                  dataIndex: "isin",
                  key: "isin",
                  render: (text) => <span className="isin-code">{text || "N/A"}</span>,
                },
                {
                  title: "Company",
                  dataIndex: "company",
                  key: "company",
                  render: (text) => <span>{text || "N/A"}</span>,
                },
                // {
                //   title: "Instrument",
                //   dataIndex: "nameOfInstrument",
                //   key: "nameOfInstrument",
                //   render: (text) => <span>{text || "N/A"}</span>,
                // },
                {
                  title: "Face Value",
                  dataIndex: "faceValue",
                  key: "faceValue",
                  render: (value) => value ? `₹${(+value).toLocaleString()}` : "N/A",
                },
                {
                  title: "Issue Date",
                  dataIndex: "issueDate",
                  key: "issueDate",
                  render: (text) => text || "N/A",
                },
                {
                  title: "Redemption",
                  dataIndex: "redemption",
                  key: "redemption",
                  render: (text) => text || "N/A",
                },
                {
                  title: "Actions",
                  key: "actions",
                  render: (_, record) => (
                    <div>
                      <Tooltip title="View">
                        <Button type="link" icon={<EyeOutlined />} onClick={() => showRecordDetails(record)} />
                      </Tooltip>
                      <Tooltip title="Edit">
                        <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                      </Tooltip>
                      <Popconfirm
                        title="Are you sure you want to delete this record?"
                        onConfirm={() => handleDelete(record.isin)}
                        onCancel={() => message.error('Delete cancelled')}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Tooltip title="Delete">
                          <Button type="link" icon={<DeleteOutlined />} danger />
                        </Tooltip>
                      </Popconfirm>
                    </div>
                  ),
                },
              ]}
              rowKey={(record, index) => index}
              pagination={false}
              className={`nsdl-table ${isDark ? "dark" : ""}`}
            />
          )}
          {/* Pagination */}
          <div style={{ textAlign: "center", marginTop: "2rem" }}>
            <Pagination
              current={pagination.current}
              total={pagination.total}
              pageSize={pagination.pageSize}
              showSizeChanger
              showQuickJumper
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total} items`
              }
              onChange={handlePageChange}
              className="custom-pagination"
            />
          </div>
          {/* Details Modal */}
          <Modal
            title={
              <span>
                Preference Share Details - {selectedRecord?.isin || ""}
              </span>
            }
            open={detailModalVisible}
            onCancel={() => setDetailModalVisible(false)}
            footer={null}
            width={800}
            destroyOnClose
          >
            {selectedRecord && (
              <Descriptions bordered column={1} size="small">
                {Object.entries(selectedRecord).map(([key, value]) => (
                  <Descriptions.Item label={camelCaseToReadable(key)} key={key}>
                    {(() => {
                      if (value === null || value === undefined || value === "") return "N/A";
                      if (key.toLowerCase().includes("date")) {
                        // Try to format as date
                        const date = new Date(value);
                        if (!isNaN(date.getTime())) return date.toLocaleDateString();
                      }
                      if (typeof value === "string" && value.includes("\n")) {
                        return <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{value}</pre>;
                      }
                      return value.toString();
                    })()}
                  </Descriptions.Item>
                ))}
              </Descriptions>
            )}
          </Modal>
          {/* Edit Modal */}
          {editModalVisible && (
            <Modal
              title="Edit Record"
              visible={editModalVisible}
              onCancel={() => setEditModalVisible(false)}
              footer={null}
            >
              <Form
                initialValues={editData}
                onFinish={handleUpdate}
                layout="vertical"
                ref={editForm}
              >
                <Form.Item noStyle>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                    {(() => {
                      const fields = Object.entries(editData).filter(([key]) => key !== 'createdAt' && key !== 'updatedAt');
                      const items = [];
                      for (let i = 0; i < fields.length; i += 2) {
                        const [key1] = fields[i];
                        const [key2] = fields[i + 1] || [];
                        items.push(
                          <div key={key1 + (key2 || '')} style={{ display: 'flex', width: '100%', gap: 16 }}>
                            <Form.Item
                              key={key1}
                              label={camelCaseToReadable(key1)}
                              name={key1}
                              rules={key1 === "isin" ? [] : [{ required: false }]}
                              style={{ flex: 1 }}
                            >
                              {key1 === "isin" ? <Input disabled /> : <Input />}
                            </Form.Item>
                            {key2 && (
                              <Form.Item
                                key={key2}
                                label={camelCaseToReadable(key2)}
                                name={key2}
                                rules={key2 === "isin" ? [] : [{ required: false }]}
                                style={{ flex: 1 }}
                              >
                                {key2 === "isin" ? <Input disabled /> : <Input />}
                              </Form.Item>
                            )}
                          </div>
                        );
                      }
                      return items;
                    })()}
                  </div>
                </Form.Item>
                <div style={{ textAlign: "right" }}>
                  <Button onClick={() => setEditModalVisible(false)} style={{ marginRight: 8 }}>
                    Cancel
                  </Button>
                  <Button type="primary" htmlType="submit" loading={editLoading}>
                    Update
                  </Button>
                </div>
              </Form>
            </Modal>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreferenceShares;
